import React, { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { dashboardService } from '../services';
import {
  ALL_SUBSCRIBED_MODULE_CODES,
  getModuleCodesForNavKeys,
} from '../navigation/navAccess';

const NAV_SCOPE_STORAGE_KEY = 'pm_nav_scope_v2';

/** Hydrate from sessionStorage only if cache was written for the current tenantId. */
function readCachedNavScopeState() {
  if (typeof window === 'undefined') return null;
  const currentTid = parseInt(localStorage.getItem('tenantId') || '0', 10);
  const raw = sessionStorage.getItem(NAV_SCOPE_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const cachedTid = parsed?.tenantId;
    if (currentTid > 0) {
      if (cachedTid === undefined || cachedTid === null) return null;
      if (Number(cachedTid) !== currentTid) return null;
    } else if (cachedTid != null && Number(cachedTid) > 0) {
      return null;
    }
    const active = !!parsed?.scopeActive;
    const keysRaw = parsed?.allowedNavKeys;
    return {
      loading: false,
      scopeActive: active,
      allowedKeys:
        active && Array.isArray(keysRaw)
          ? new Set(keysRaw.map((k) => String(k).toUpperCase()))
          : active
            ? new Set()
            : null,
      appCodes: parsed?.appCodes ?? null,
    };
  } catch {
    return null;
  }
}

const NavigationAccessContext = createContext(null);

/** Stable key for remounting the nav provider whenever tenant or user changes (login / logout). */
export function getSessionIdentityKey() {
  if (typeof window === 'undefined') return 'anon';
  try {
    const tid = localStorage.getItem('tenantId') || '';
    const userStr = localStorage.getItem('user');
    const u = userStr ? JSON.parse(userStr) : null;
    const uid = u?.userId ?? u?.UserId ?? '';
    return `${tid}:${uid}`;
  } catch {
    return `${localStorage.getItem('tenantId') || ''}:err`;
  }
}

/**
 * Wraps NavigationAccessProvider with a key tied to tenant+user so switching tenant or account
 * fully remounts nav state (no stale subscribedModules / scope from the previous session).
 * Listens for `pm_session_identity_changed` (login/logout) and `storage` (cross-tab).
 */
export function NavigationAccessBoundary({ children }) {
  const [sessionKey, setSessionKey] = useState(() => getSessionIdentityKey());

  useEffect(() => {
    const sync = () => {
      setSessionKey((prev) => {
        const next = getSessionIdentityKey();
        return prev === next ? prev : next;
      });
    };
    window.addEventListener('pm_session_identity_changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('pm_session_identity_changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return (
    <NavigationAccessProvider key={sessionKey}>
      {children}
    </NavigationAccessProvider>
  );
}

export const NavigationAccessProvider = ({ children }) => {
  const { hasPermission } = usePermissions();
  /** Invalidates in-flight getNavAppScope so a response from the previous tenant/session cannot overwrite after login/logout/refresh. */
  const navScopeFetchGenRef = useRef(0);

  const [navAppScope, setNavAppScope] = useState(() => {
    return (
      readCachedNavScopeState() ?? {
        loading: true,
        scopeActive: false,
        allowedKeys: null,
        appCodes: null,
      }
    );
  });

  const fetchScope = useCallback(async () => {
    const fetchGen = ++navScopeFetchGenRef.current;
    try {
      const data = await dashboardService.getNavAppScope();

      if (fetchGen !== navScopeFetchGenRef.current) {
        return;
      }

      const tid = typeof window !== 'undefined'
        ? parseInt(localStorage.getItem('tenantId') || '0', 10)
        : 0;

      if (typeof window !== 'undefined') {
        sessionStorage.setItem(
          NAV_SCOPE_STORAGE_KEY,
          JSON.stringify({
            tenantId: tid,
            scopeActive: !!data?.scopeActive,
            allowedNavKeys: data?.allowedNavKeys,
            appCodes: data?.appCodes,
          }),
        );
      }

      const active = !!data?.scopeActive;
      const raw = data?.allowedNavKeys;
      const allowedKeys =
        active && Array.isArray(raw)
          ? new Set(raw.map((k) => String(k).toUpperCase()))
          : active
            ? new Set()
            : null;

      setNavAppScope({
        loading: false,
        scopeActive: active,
        allowedKeys,
        appCodes: data?.appCodes || null
      });
    } catch (err) {
      console.error("[NavigationAccess] Fetch failed", err);
      if (fetchGen !== navScopeFetchGenRef.current) {
        return;
      }
      setNavAppScope({
        loading: false,
        scopeActive: false,
        allowedKeys: null,
        appCodes: null
      });
    }
  }, []);

  const refreshAccess = useCallback(() => {
    setNavAppScope({
      loading: true,
      scopeActive: false,
      allowedKeys: null,
      appCodes: null,
    });
    fetchScope();
  }, [fetchScope]);

  useEffect(() => {
    fetchScope();

    /** Soft refresh: refetch without forcing loading:true (avoids Apps menu spinner on login/refresh). */
    const handleGlobalEvent = () => fetchScope();
    /**
     * Login/logout: bump generation and clear scope in React immediately so UI (Apps launcher, nav)
     * does not keep showing the previous tenant for a frame while refresh is queued.
     */
    const handleInvalidate = () => {
      navScopeFetchGenRef.current += 1;
      setNavAppScope({
        loading: true,
        scopeActive: false,
        allowedKeys: null,
        appCodes: null,
      });
    };

    window.addEventListener('pm_nav_scope_refresh', handleGlobalEvent);
    window.addEventListener('pm_nav_scope_invalidate', handleInvalidate);

    return () => {
      window.removeEventListener('pm_nav_scope_refresh', handleGlobalEvent);
      window.removeEventListener('pm_nav_scope_invalidate', handleInvalidate);
      // Invalidate any in-flight getNavAppScope so it cannot call setState after unmount (tenant switch remount)
      navScopeFetchGenRef.current += 1;
    };
  }, [fetchScope]);

  const subscribedModules = useMemo(() => {
    if (typeof window === 'undefined') return new Set();

    // While scope is loading: do NOT read tenant.appCodes from localStorage — those client hints
    // can belong to the previous tenant (spread-merge in profile updates) and caused the Apps
    // launcher to show the last tenant's modules until refresh. Server scope is the source of truth.
    if (navAppScope.loading) {
      return ALL_SUBSCRIBED_MODULE_CODES;
    }

    // Legacy / unconfigured tenant: API returns scopeActive=false (no EnabledNavAppCodesJson/Keys).
    // Do not treat empty appCodes as "subscribed to nothing" — align with backend (no module filter).
    if (!navAppScope.scopeActive) {
      return ALL_SUBSCRIBED_MODULE_CODES;
    }

    // Explicit nav-app scope: merge app codes from admin with modules implied by allowed nav keys
    // (covers granular-only tenants and mixed app + key overrides).
    const codesFromApps = Array.isArray(navAppScope.appCodes)
      ? navAppScope.appCodes.map((m) => String(m).toUpperCase()).filter(Boolean)
      : [];
    const fromKeys = navAppScope.allowedKeys
      ? getModuleCodesForNavKeys(navAppScope.allowedKeys)
      : new Set();

    const merged = new Set([...codesFromApps, ...fromKeys]);
    if (merged.size > 0) return merged;

    // Configured scope but nothing resolved — hide module-gated entries
    return new Set();
  }, [
    navAppScope.scopeActive,
    navAppScope.appCodes,
    navAppScope.allowedKeys,
    navAppScope.loading,
  ]);

  const value = useMemo(() => ({
    featuresLoading: navAppScope.loading,
    hasPermission,
    subscribedModules,
    navAppScopeActive: navAppScope.scopeActive,
    navAppAllowedKeys: navAppScope.allowedKeys,
    refreshAccess
  }), [navAppScope, hasPermission, subscribedModules, refreshAccess]);

  return (
    <NavigationAccessContext.Provider value={value}>
      {children}
    </NavigationAccessContext.Provider>
  );
};

export const useNavigationAccessContext = () => {
  const context = useContext(NavigationAccessContext);
  if (!context) {
    throw new Error('useNavigationAccess must be used within a NavigationAccessProvider');
  }
  return context;
};
